/**
 * Campagnes Meta de la période, par dépense.
 *
 * ROAS et achats sont ceux qu'ATTRIBUE Meta : utiles pour comparer les
 * campagnes entre elles, pas pour juger la rentabilité globale — c'est le rôle
 * du ROAS calculé sur le CA boutique réel, dans les tuiles au-dessus.
 */

import { formatDecimal, formatMoney } from '../lib/format';
import { relativeChange } from '../lib/sales';
import { IMPACT_TONE_COLORS } from '../lib/theme';
import { relativeVerdict, verdictGlyph, verdictLabel } from '../lib/verdict';
import type { AdsCampaignFigures, AdsCampaignRow } from '../types';
import { Card } from './Card';

interface AdsCampaignsTableProps {
  rows: readonly AdsCampaignRow[];
  currency: string;
  comparisonsOverlap: boolean;
  /** Plateforme qui attribue les achats : « Meta » (défaut) ou « Amazon ». */
  platform?: string;
}

const roas = (figures: AdsCampaignFigures) =>
  figures.spend > 0 ? `×${formatDecimal(figures.metaPurchaseValue / figures.spend)}` : '—';

/** Écart de dépense : direction seulement, couleur neutre — dépenser plus n'est ni bien ni mal. */
function SpendDelta({ current, reference }: { current: number; reference: number }) {
  if (reference === 0) return <td className="num" style={{ color: IMPACT_TONE_COLORS.neutral }}>nouvelle</td>;
  const change = relativeChange(current, reference);
  const verdict = relativeVerdict(change, 5);
  return (
    <td className="num" style={{ color: IMPACT_TONE_COLORS.neutral }}>
      <span aria-hidden="true">{verdictGlyph(verdict)}</span>{' '}
      {change === null ? '—' : `${change >= 0 ? '+' : '−'}${Math.abs(change).toLocaleString('fr-FR')} %`}
      <span className="sr-only"> — {verdictLabel(verdict)}</span>
    </td>
  );
}

export function AdsCampaignsTable({ rows, currency, comparisonsOverlap, platform = 'Meta' }: AdsCampaignsTableProps) {
  return (
    <Card
      title={`Campagnes ${platform}`}
      subtitle={`Par dépense sur la période — achats et ROAS attribués par ${platform}`}
      className="chart-grid__wide"
    >
      {rows.length === 0 ? (
        <p className="empty">Aucune dépense sur cette période.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Campagne</th>
                <th className="num">Dépense HT</th>
                <th className="num">Achats {platform}</th>
                <th className="num">Valeur {platform}</th>
                <th className="num">ROAS {platform}</th>
                {!comparisonsOverlap && <th className="num">Dépense HT vs préc.</th>}
                <th className="num">Dépense HT vs an dernier</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.campaignId}>
                  <td className="wrap">{row.name}</td>
                  <td className="num">{formatMoney(row.current.spend, currency)}</td>
                  <td className="num">{formatDecimal(row.current.metaPurchases)}</td>
                  <td className="num">{formatMoney(row.current.metaPurchaseValue, currency)}</td>
                  <td className="num">{roas(row.current)}</td>
                  {!comparisonsOverlap && <SpendDelta current={row.current.spend} reference={row.previous.spend} />}
                  <SpendDelta current={row.current.spend} reference={row.yearAgo.spend} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
